/**
 * User Permissions Management Component
 * Allows Admin/SuperAdmin to manage document viewing permissions for users
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, Check, X, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'react-toastify';

const UserPermissions = () => {
  const API = process.env.REACT_APP_BACKEND_URL || '';
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
      toast.error(error.response?.data?.detail || 'Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const toggleDocumentPermission = async (user) => {
    setConfirmModal({
      user,
      newValue: !user.can_view_all_documents
    });
  };

  const confirmPermissionChange = async () => {
    if (!confirmModal) return;

    try {
      setUpdating(confirmModal.user.id);
      const token = localStorage.getItem('token');
      
      const response = await axios.patch(
        `${API}/api/users/${confirmModal.user.id}/permissions`,
        {
          can_view_all_documents: confirmModal.newValue
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchUsers();
        setConfirmModal(null);
      }
    } catch (error) {
      console.error('Yetki güncelleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Yetki güncellenemedi');
    } finally {
      setUpdating(null);
    }
  };

  const getRoleBadgeColor = (roleName) => {
    const role = roleName?.toLowerCase() || '';
    if (role.includes('super') || role.includes('süper')) {
      return 'bg-red-100 text-red-700 border-red-200';
    } else if (role.includes('admin')) {
      return 'bg-orange-100 text-orange-700 border-orange-200';
    } else {
      return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getRoleDisplayName = (roleName) => {
    const role = roleName?.toLowerCase() || '';
    if (role.includes('super') || role.includes('süper')) {
      return 'Süper Admin';
    } else if (role.includes('admin')) {
      return 'Admin';
    } else {
      return 'Kullanıcı';
    }
  };

  const isRoleProtected = (roleName) => {
    const role = roleName?.toLowerCase() || '';
    return role.includes('super') || role.includes('süper') || role.includes('admin');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-50 rounded-lg">
            <Shield className="w-6 h-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-800">Kullanıcı Belge Erişim Yetkileri</h2>
            <p className="text-sm text-gray-600 mt-1">
              Kullanıcıların belge görüntüleme yetkilerini yönetin. 
              Normal kullanıcılar sadece kendi yükledikleri belgeleri görebilir, 
              ancak özel yetki vererek tüm şirket belgelerini görmelerini sağlayabilirsiniz.
            </p>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <strong>Not:</strong> Admin ve Süper Admin rolleri otomatik olarak tüm belgeleri görme yetkisine sahiptir.
              Bu yetkiler değiştirilemez.
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kullanıcı
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  E-posta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tüm Belgeleri Görme
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  İşlem
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map((user) => {
                const isProtected = isRoleProtected(user.role_name);
                const hasPermission = user.can_view_all_documents || isProtected;

                return (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-10 w-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {user.full_name?.[0]?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name || 'İsimsiz'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role_name)}`}>
                        {getRoleDisplayName(user.role_name)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {isProtected ? (
                          <span className="inline-flex items-center gap-1.5 text-sm text-green-600 font-medium">
                            <Check className="w-4 h-4" />
                            Her zaman aktif
                          </span>
                        ) : (
                          <div className="flex items-center gap-3">
                            {hasPermission ? (
                              <span className="inline-flex items-center gap-1.5 text-sm text-green-600 font-medium">
                                <Eye className="w-4 h-4" />
                                Tüm belgeleri görebilir
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
                                <EyeOff className="w-4 h-4" />
                                Sadece kendi belgeleri
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isProtected ? (
                        <span className="text-xs text-gray-400">Korumalı</span>
                      ) : (
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={hasPermission}
                            onChange={() => toggleDocumentPermission(user)}
                            disabled={updating === user.id}
                            className="sr-only peer"
                          />
                          <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${updating === user.id ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                        </label>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">Kullanıcı bulunamadı</p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setConfirmModal(null)}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start gap-4 mb-4">
              <div className={`p-3 rounded-lg ${confirmModal.newValue ? 'bg-green-50' : 'bg-orange-50'}`}>
                <Shield className={`w-6 h-6 ${confirmModal.newValue ? 'text-green-600' : 'text-orange-600'}`} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-800">
                  Yetki {confirmModal.newValue ? 'Verme' : 'Kaldırma'} Onayı
                </h3>
              </div>
            </div>

            <div className="mb-6">
              <p className="text-sm text-gray-700 mb-3">
                <strong>{confirmModal.user.full_name}</strong> kullanıcısının 
                "Tüm Belgeleri Görme" yetkisini{' '}
                <strong className={confirmModal.newValue ? 'text-green-600' : 'text-orange-600'}>
                  {confirmModal.newValue ? 'vermek' : 'kaldırmak'}
                </strong> istediğinizden emin misiniz?
              </p>

              {confirmModal.newValue ? (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-800">
                    <strong>Sonuç:</strong> Bu kullanıcı tüm şirketin belgelerini görüntüleyebilecek.
                  </p>
                </div>
              ) : (
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-800">
                    <strong>Sonuç:</strong> Bu kullanıcı artık sadece kendi yüklediği belgeleri görebilecek.
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirmModal(null)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={updating}
              >
                İptal
              </button>
              <button
                onClick={confirmPermissionChange}
                disabled={updating}
                className={`flex-1 px-4 py-2 rounded-lg text-white transition-colors ${
                  confirmModal.newValue
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-orange-600 hover:bg-orange-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {updating ? 'İşleniyor...' : `Evet, ${confirmModal.newValue ? 'Ver' : 'Kaldır'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPermissions;
